import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import io from "socket.io-client";
import './i18n';
import { useTranslation } from 'react-i18next';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let socket = null;

import { createContext, useContext } from "react";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  
  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      // Force update by setting a timestamp (triggers re-render)
      setUser(prev => prev ? {...prev} : null);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const previousBalance = user?.credits_balance || 0;
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const newBalance = response.data.credits_balance;
      
      // Check if user just received welcome bonus
      if (previousBalance === 0 && newBalance === 5) {
        const events_log = response.data.events_log || [];
        const welcomeEvent = events_log.find(e => e.event === 'welcome_bonus');
        if (welcomeEvent) {
          toast.success('🎉 Welcome to Chronos!', {
            description: `You received ${welcomeEvent.credits} time credits to get started!`
          });
        }
      }
      
      setUser(response.data);
      
      // Set user's preferred language
      if (response.data.preferred_language) {
        i18n.changeLanguage(response.data.preferred_language);
      }
      
      if (!socket) {
        socket = io(BACKEND_URL, {
          transports: ['websocket', 'polling']
        });
        
        socket.on('connect', () => {
          socket.emit('join_room', { user_id: response.data.id });
        });
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      if (socket) {
        socket.disconnect();
        socket = null;
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, logout, socket }}>
      {children}
    </AuthContext.Provider>
  );
}

import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import BrowseUsers from "./pages/BrowseUsers";
import Transactions from "./pages/Transactions";
import Messages from "./pages/Messages";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentRequired from "./pages/PaymentRequired";
import PaymentSuccess from "./pages/PaymentSuccess";
import OnboardingTour from "./components/OnboardingTour";
import LilithChat from "./components/LilithChat";
import FAQ from "./pages/FAQ";
import Analytics from "./pages/Analytics";
import Referrals from "./pages/Referrals";
import Groups from "./pages/Groups";
import GroupChat from "./pages/GroupChat";
import Privacy from "./pages/Privacy";
import CreatorDashboard from "./pages/CreatorDashboard";
import Settings from "./pages/Settings";
import CoursesMarketplace from "./pages/CoursesMarketplace";
import CourseDetails from "./pages/CourseDetails";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/" />;
  
  // Redirect to payment if not premium
  if (!user.premium_status) {
    return <Navigate to="/payment-required" />;
  }
  
  return children;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const processSession = async () => {
      if (processing) return;
      
      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (sessionId) {
        setProcessing(true);
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );

          document.cookie = `session_token=${response.data.session_token}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;

          window.history.replaceState({}, document.title, '/dashboard');

          await checkAuth();
          
          toast.success('Welcome to Chronos!');
          navigate('/payment-required');
        } catch (error) {
          console.error('Session error:', error);
          toast.error('Authentication failed');
          navigate('/');
        }
      }
    };

    processSession();
  }, [location, navigate, checkAuth, processing]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-500 mx-auto mb-4"></div>
        <p className="text-gray-600">Processing authentication...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "test", currency: "GBP" }}>
      <AuthProvider>
        <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<AuthCallback />} />
            <Route path="/payment-required" element={<PaymentRequired />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/app" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/browse" element={<PrivateRoute><BrowseUsers /></PrivateRoute>} />
            <Route path="/transactions" element={<PrivateRoute><Transactions /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
            <Route path="/referrals" element={<PrivateRoute><Referrals /></PrivateRoute>} />
            <Route path="/groups" element={<PrivateRoute><Groups /></PrivateRoute>} />
            <Route path="/groups/:groupId" element={<PrivateRoute><GroupChat /></PrivateRoute>} />
            <Route path="/privacy" element={<PrivateRoute><Privacy /></PrivateRoute>} />
            <Route path="/creator" element={<PrivateRoute><CreatorDashboard /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/courses" element={<CoursesMarketplace />} />
            <Route path="/courses/:courseId" element={<CourseDetails />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        <OnboardingTour />
        <LilithChat />
      </div>
    </AuthProvider>
    </PayPalScriptProvider>
  );
}

export default App;
export { API, socket };